import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';
import { faVideo } from '@fortawesome/pro-solid-svg-icons/faVideo';
import { faImage } from '@fortawesome/pro-solid-svg-icons/faImage';
import { TypedMemo } from '../../../../../common/types';
import { getFullAPIFileURL } from '../../../../../common/getFullAPIFileURL';
import type { ReleasePlanDay } from '../../../../../../redux/api/fymAPI.types';
import { rolloutMessages } from './rolloutMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the run sheet; each part uses its own slice of it */
import styles from './Rollout.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StepThumbProps {
  day: ReleasePlanDay;
}

/**
 * The picture of a step (design rollout-plan `.th`, `.ghost`): the poster of its file, a play mark
 * on a video, the render percent while it is made, or a dashed ghost when the asset is missing.
 */
const StepThumbComponent: React.FC<StepThumbProps> = ({ day }) => {
  const intl = useIntl();
  const { asset } = day;
  const isVideo = day.format === 'reel' || asset?.themeId === 'aiReel';
  const poster = asset?.posterUrl ?? null;
  if (asset === null || (asset.status === 'missing' && poster === null)) {
    return (
      <span className={styles.ghost} aria-hidden>
        <FontAwesomeIcon icon={isVideo ? faVideo : faImage} />
      </span>
    );
  }
  return (
    <span className={styles.thumb} aria-hidden>
      {poster !== null && <img src={getFullAPIFileURL(poster)} alt="" />}
      {poster === null && <FontAwesomeIcon icon={isVideo ? faVideo : faImage} />}
      {isVideo && poster !== null && asset.status === 'ready' && (
        <span className={styles.play}>
          <FontAwesomeIcon icon={faPlay} />
        </span>
      )}
      {asset.status === 'working' && (
        <span className={styles.making}>
          {intl.formatMessage(rolloutMessages.making, { percent: asset.making ?? 0 })}
        </span>
      )}
    </span>
  );
};

export const StepThumb = TypedMemo(StepThumbComponent);
